import type { Chess } from "@vendor/chess.js";

/**
 * Theme detection written as code, for the courses whose ideas need more than
 * the data format of a Black course or a coaching pack (see themes.ts). It
 * lives with the course content so a course's lazy chunk can carry its own
 * detectors without importing the trainer.
 */

export type ThemeState = "done" | "pending" | "missed";

export type ThemeContext = {
  /** White's moves in SAN with check and mate suffixes removed. */
  white: string[];
  /** All moves in SAN, both sides, suffixes removed. */
  all: string[];
  chess: Chess;
  /** White castled at some point, course move or not: the king is safe either way. */
  castled?: boolean;
  /** White's moves as played, course moves or not. */
  played?: string[];
};

export type CourseTheme = {
  id: string;
  title: string;
  idea: string;
  /** Main-line move number at which the course realizes the idea. */
  mainlineMove: number;
  detect: (ctx: ThemeContext) => ThemeState;
};

export function clean(san: string): string {
  return san.replace(/[+#]/g, "");
}

export function played(ctx: ThemeContext, ...sans: string[]): boolean {
  return sans.some((san) => ctx.white.includes(san));
}

export function indexOfWhite(ctx: ThemeContext, ...sans: string[]): number {
  const hits = sans.map((san) => ctx.white.indexOf(san)).filter((i) => i >= 0);
  return hits.length ? Math.min(...hits) : -1;
}

export function pieceOn(
  ctx: ThemeContext,
  square: string,
  type: string,
  color: "w" | "b"
): boolean {
  const piece = ctx.chess.get(square);
  return Boolean(piece && piece.type === type && piece.color === color);
}

export function isLightSquare(square: string): boolean {
  return (square.charCodeAt(0) - 97 + Number(square[1])) % 2 === 0;
}

export function bishopOnSquareColor(ctx: ThemeContext, color: "w" | "b", light: boolean): boolean {
  const board = ctx.chess.board();
  for (let row = 0; row < 8; row += 1) {
    for (let file = 0; file < 8; file += 1) {
      const piece = board[row][file];
      if (!piece || piece.type !== "b" || piece.color !== color) continue;
      const square = `${"abcdefgh"[file]}${8 - row}`;
      if (isLightSquare(square) === light) return true;
    }
  }
  return false;
}

export function whiteCastled(ctx: ThemeContext): boolean {
  return ctx.castled ?? played(ctx, "O-O", "O-O-O");
}

export function castlingLost(ctx: ThemeContext): boolean {
  const rights = ctx.chess.fen().split(" ")[2] || "";
  return !/[KQ]/.test(rights);
}

export function donePendingMissed(done: boolean, missed: boolean): ThemeState {
  if (done) return "done";
  if (missed) return "missed";
  return "pending";
}

export const castleTheme = (mainlineMove: number): CourseTheme => ({
  id: "castle",
  title: "Castle before the center opens",
  idea: "Move the king off the central files before pawn exchanges open lines.",
  mainlineMove,
  detect: (ctx) => donePendingMissed(whiteCastled(ctx), !whiteCastled(ctx) && castlingLost(ctx)),
});
